import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import {
    Avatar, Card, CardActions, CardContent, CardMedia, Checkbox,
    Chip, FormControl, FormControlLabel, InputLabel,
    MenuItem, Select, TextField, Autocomplete, Grid, Typography, Box
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import { PAContext } from "../../../Services/PAContext";
import country_codes from "../../../sampleData/PhonePrefixes";
import { supportedLanguages } from "../../../sampleData/PreferredLanguages";
import { _airports_list } from "../../../sampleData/airline_data";
import { cancelChipStyle } from "../../ButtonStyle/Cancel";
import { updateChipStyle } from "../../ButtonStyle/Update";
import {useGoogleAutocompleteUniversal} from "../../Helpers/GoogleAutocomplete";



function AttendeeEdit(props) {

    const navigate = useNavigate();
    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { authenticated } = dataArray;

    // --- Consolidated State ---
    const [formData, setFormData] = useState({
        fname: "", lname: "", nickname: "",
        address: "", city: "", state: "", zip: "", country: "",
        email: "", phone_prefix: "", phone: "",
        dob: "", language: "en", preferred_airport: ""
    });

    const [avatars, setAvatars] = useState({ original: {}, updated: {}, changed: false });

    // --- Load Data ---
    useEffect(() => {
        const attendee = PA_GlobalObject.Planning?.attendees?.find(a => a.id === props.editAttendeeId);
        if (attendee) {
            setFormData({
                fname: attendee.fname || "",
                lname: attendee.lname || "",
                nickname: attendee.nickname || "",
                address: attendee.address || "",
                city: attendee.city || "",
                state: attendee.state || "",
                zip: attendee.zip || "",
                country: attendee.country || "",
                email: attendee.email || "",
                phone_prefix: attendee.phone_prefix || "",
                phone: attendee.phone || "",
                dob: attendee.dob || "",
                language: attendee.language || "en",
                preferred_airport: attendee.preferred_airport || ""
            });
            setAvatars(prev => ({ ...prev, original: attendee.avatar, updated: attendee.avatar }));
        }
    }, [props.editAttendeeId, PA_GlobalObject.Planning.attendees]);

    // --- Helpers ---
    const capitalize = (val) => val.replace(/\b\w/g, l => l.toUpperCase());

    const handleInputChange = (e) => {
        const { id, name, value, checked, type } = e.target;
        const field = id || name;
        let finalValue = type === 'checkbox' ? checked : value;

        if (['fname', 'lname', 'nickname'].includes(field)) {
            finalValue = capitalize(finalValue);
        }

        setFormData(prev => ({
            ...prev,
            [field]: finalValue,
            // Sync nickname with first name automatically
            ...(field === 'fname' ? { nickname: finalValue } : {})
        }));
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64 = reader.result;
            setAvatars(prev => ({
                ...prev,
                changed: true,
                updated: { preview: base64, name: file.name, type: file.type, size: file.size, hasBeenUpdated: true }
            }));
        };
        reader.readAsDataURL(file);
    };

    const parseV2Address = (place) => {
        let _address_line = "", _address_city = "", _address_state = "", _address_zip = "", _address_country = "";
        const components = place.addressComponents || [];
        const _country_comp = components.find(c => c.types.includes("country"));
        const _country_code = _country_comp?.shortText || "";

        components.forEach((component) => {
            const types = component.types;
            const val = component.longText;
            if (types.includes("street_number")) _address_line = val;
            else if (types.includes("route")) _address_line = _address_line ? `${_address_line} ${val}` : val;
            else if (types.includes("locality")) _address_city = val;
            else if (types.includes("administrative_area_level_1")) _address_state = (_country_code === "US") ? component.shortText : val;
            else if (types.includes("postal_code")) _address_zip = val;
            else if (types.includes("country")) _address_country = component.shortText;
        });

        if (!_address_line && place.formattedAddress) {
            _address_line = place.formattedAddress.split(',')[0].trim();
        }
        return { _address_line, _address_city, _address_state, _address_zip, _address_country };
    };
    const {
        containerRef: attendeeAddressLocationRef,
        initAutocomplete: initUniversal
    } = useGoogleAutocompleteUniversal((place) => {
        // This callback fires when a user selects a place
        const parsed = parseV2Address(place);

        // Update the consolidated formData with the parsed pieces
        setFormData(prev => ({
            ...prev,
            address: parsed._address_line,
            city: parsed._address_city,
            state: parsed._address_state,
            zip: parsed._address_zip,
            country: parsed._address_country
        }));
    });

    // 2. Trigger the initialization once the component mounts
    useEffect(() => {
        if (window.google) {
            initUniversal();
        }
    }, [initUniversal]);

    // --- Final Save ---
    const handleSave = () => {
        if (!formData.fname || !formData.lname) return;

        const attendeeId = props.editAttendeeId;
        const finalData = { ...formData, avatar: avatars.updated };

        // Local Update
        set_PA_GlobalObject(prev => ({
            ...prev,
            Planning: {
                ...prev.Planning,
                attendees: prev.Planning.attendees.map(a => a.id === attendeeId ? { ...a, ...finalData } : a)
            }
        }));

        // Server Update
        if (authenticated && PA_GlobalObject.Planning.status !== 10) {
            sendSocketMessage({
                action: "E_updateEventAttendeeFields",
                event_id: PA_GlobalObject.Planning.id,
                attendee_id: attendeeId,
                new_avatar: avatars.changed,
                avatar: avatars.changed ? avatars.updated : undefined,
                ...formData,
            });
        }
        navigate('/Planner/Attendees');
    };

    return (
        <div className="attendee-list-container">
            <div className="attendee-list-header">
                <div className="attendee-list-header-text"><h5>Edit {formData.fname} {formData.lname}</h5></div>
                <div className="attendee-list-header-chip-holder">
                    <Chip sx={cancelChipStyle} label="Cancel" size="small" onClick={() => navigate('/Planner/Attendees')} />
                    <Chip sx={updateChipStyle} label="Update" size="small" onClick={handleSave} />
                </div>
            </div>

            <div className={props.showingPageHeader ? "attendee-list-items-container with-header" : "attendee-list-items-container"}>
                <Grid container spacing={2} direction="column">
                    <Grid item>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <input type="file" id="file-input" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />
                            <Avatar
                                src={avatars.updated?.preview || avatars.original?.preview || ''}
                                sx={{ width: 64, height: 64, cursor: 'pointer' }}
                                onClick={() => document.getElementById('file-input').click()}
                            />
                            <Typography variant="body2" color="text.secondary">Click to replace photo</Typography>
                        </Box>
                    </Grid>

                    {['fname', 'lname', 'nickname', 'address', 'city', 'state', 'zip', 'country', 'email', 'phone'].map((field) => {
                        // SPECIAL CASE: Address Autocomplete
                        if (field === 'address') {
                            return (
                                <Grid item key={field} sx={{ mb: 1 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            mb: 0.5,
                                            fontWeight: 700,
                                            color: 'text.secondary',
                                            ml: 1
                                        }}
                                    >
                                        SEARCH & SELECT ADDRESS
                                    </Typography>
                                    <Box
                                        ref={attendeeAddressLocationRef}
                                        sx={{
                                            flex: 1,
                                            minHeight: '56px', // Matches standard MUI TextField height
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            transition: 'border-color 0.2s',
                                            '&:hover': {
                                                borderColor: 'rgba(0, 0, 0, 0.87)',
                                            },
                                            '&:focus-within': {
                                                borderColor: '#1976d2',
                                                borderWidth: '2px',
                                                px: '11px', // Adjust padding for 2px border to prevent "jumping"
                                            },
                                            '& gmp-place-autocomplete': {
                                                width: '100%',
                                                display: 'block',
                                            }
                                        }}
                                    />
                                    {/* Optional: Read-only display of the currently parsed address line */}
                                    {formData.address && (
                                        <Typography variant="caption" sx={{ ml: 1, mt: 0.5, fontStyle: 'italic', color: 'primary.main' }}>
                                            Selected: {formData.address}
                                        </Typography>
                                    )}
                                </Grid>
                            );
                        }

                        // STANDARD CASE: All other fields
                        return (
                            <Grid item key={field}>
                                <TextField
                                    fullWidth
                                    id={field}
                                    label={field.replace('_', ' ').toUpperCase()}
                                    value={formData[field]}
                                    onChange={handleInputChange}
                                    required={['fname', 'lname'].includes(field)}
                                    // Show helper text for phone if prefix is +1
                                    helperText={field === 'phone' && formData.phone_prefix === '+1' ? 'Format: (555) 555-5555' : ''}
                                />
                            </Grid>
                        );
                    })}

                    <Grid item>
                        <FormControl fullWidth>
                            <InputLabel>Phone Prefix</InputLabel>
                            <Select name="phone_prefix" value={formData.phone_prefix} onChange={handleInputChange} label="Phone Prefix">
                                {country_codes.map((item, i) => (
                                    <MenuItem key={i} value={item.dialCode}>{item.dialCode} : {item.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item>
                        <DatePicker
                            sx={{ width: '100%' }}
                            label="Date of Birth"
                            value={formData.dob ? dayjs(formData.dob) : null}
                            onChange={(val) => setFormData(p => ({ ...p, dob: val?.format("YYYY-MM-DD") || "" }))}
                            maxDate={dayjs()}
                        />
                    </Grid>

                    <Grid item>
                        <FormControl fullWidth>
                            <InputLabel id="language-label">Preferred Language</InputLabel>
                            <Select
                                labelId="language-label"
                                name="language"
                                value={formData.language}
                                label="Preferred Language"
                                onChange={handleInputChange}
                            >
                                {supportedLanguages.map((lang) => (
                                    <MenuItem key={lang.code} value={lang.code}>
                                        {lang.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item>
                        <Autocomplete
                            options={_airports_list}
                            getOptionLabel={(o) => `${o.iata} : ${o.name}`}
                            value={_airports_list.find(a => a.iata === formData.preferred_airport) || null}
                            onChange={(_, val) => setFormData(p => ({ ...p, preferred_airport: val?.iata || "" }))}
                            renderInput={(params) => <TextField {...params} label="Preferred Airport" />}
                        />
                    </Grid>

                </Grid>
            </div>
        </div>
    );
}

export default AttendeeEdit;